import React from 'react';
import { Menu, Tag } from 'antd';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  UserOutlined,
  HourglassOutlined,
  HomeOutlined,
  DollarOutlined,
  ProfileOutlined,
  WalletOutlined,
  AuditOutlined,
  LockOutlined,
  ContactsOutlined,
  FileTextOutlined,
  BankOutlined,
} from '@ant-design/icons';

import { IState } from './redux/reducers';

import { Scope } from 'redux/reducers/authReducer';
import { usePermission } from 'hooks/usePermission';
import { useIsReadOnly, useScreenSize } from 'hooks';

interface RenderMenuItemProps {
  key: string;
  title: string | React.ReactNode;
  icon: React.ReactNode;
  subscriptionRequired?: boolean;
  hasPermission?: boolean;
}

interface AppMenuProps {
  setCollapsed: (value: boolean) => void;
}

export function AppMenu(props: AppMenuProps) {
  const isSmallScreen = useScreenSize();
  const history = useHistory();
  const location = useLocation();

  const isReadOnly = useIsReadOnly();

  const authState = useSelector((state: IState) => state.auth);

  const hasPermissionContactsRead = usePermission(Scope.ContactsRead);
  const hasPermissionAgreementsRead = usePermission(Scope.AgreementsRead);
  const hasPermissionRatesRead = usePermission(Scope.RatesRead);
  const hasPermissionShiftsRead = usePermission(Scope.ShiftsRead);
  const hasPermissionInvoicesRead = usePermission(Scope.InvoicesRead);
  const hasPermissionExpensesRead = usePermission(Scope.ExpensesRead);
  const hasPermissionReportsShift = usePermission(Scope.ReportsShift);
  const hasPermissionReportsKm = usePermission(Scope.ReportsKms);
  const hasPermissionReportsTax = usePermission(Scope.ReportsTax);
  const hasPermissionReportsInvoice = usePermission(Scope.ReportsInvoice);
  const hasPermissionReportsIncident = usePermission(Scope.ReportsIncident);

  const onMenuSelected = (key: string | number) => {
    if (isSmallScreen && props.setCollapsed) {
      props.setCollapsed(true);
    }
    history.push(`/${key}`);
  };

  let path = location.pathname.replace(/\//g, '');
  if (path === '') {
    path = 'dashboard';
  }

  const renderMenuItem = (options: RenderMenuItemProps) => {
    let disabled = false;
    if (options.hasPermission === false) {
      return <></>;
    } else if (options.subscriptionRequired && isReadOnly) {
      disabled = true;
    }

    return (
      <Menu.Item key={options.key} disabled={disabled} className={disabled ? 'read-only' : ''} icon={disabled ? <LockOutlined /> : options.icon}>
        {options.title}
      </Menu.Item>
    );
  };

  const renderAdminMenuItem = (options: RenderMenuItemProps) => {
    if (!authState.currentUser.isAdmin) {
      return <></>;
    }
    return renderMenuItem(options);
  };

  return (
    <Menu theme="dark" mode="inline" selectedKeys={[path]} onClick={({ key }) => onMenuSelected(key)}>
      {renderMenuItem({ key: 'dashboard', title: 'Dashboard', icon: <HomeOutlined /> })}
      {renderMenuItem({ key: 'contacts', title: 'Contacts', icon: <ContactsOutlined />, hasPermission: hasPermissionContactsRead })}
      {renderMenuItem({ key: 'agreements', title: 'Service agreements', icon: <AuditOutlined />, hasPermission: hasPermissionAgreementsRead })}
      {renderMenuItem({ key: 'rates', title: 'Rates', icon: <DollarOutlined />, subscriptionRequired: true, hasPermission: hasPermissionRatesRead })}
      {renderMenuItem({ key: 'shifts', title: 'Shifts', icon: <HourglassOutlined />, subscriptionRequired: true, hasPermission: hasPermissionShiftsRead })}
      {renderMenuItem({ key: 'invoices', title: 'Invoices', icon: <ProfileOutlined />, subscriptionRequired: true, hasPermission: hasPermissionInvoicesRead })}
      {renderMenuItem({ key: 'expenses', title: 'Expenses', icon: <WalletOutlined />, subscriptionRequired: true, hasPermission: hasPermissionExpensesRead })}
      <Menu.SubMenu key="reports" title="Reports" icon={<FileTextOutlined />}>
        {renderMenuItem({ key: 'reports/incidents', title: <>Incidents <Tag color="success">NEW</Tag></>, icon: <FileTextOutlined />, subscriptionRequired: true, hasPermission: hasPermissionReportsIncident })}
        {renderMenuItem({ key: 'reports/invoices', title: 'Invoices', icon: <FileTextOutlined />, subscriptionRequired: true, hasPermission: hasPermissionReportsInvoice })}
        {renderMenuItem({ key: 'reports/kms', title: 'Kilometres', icon: <FileTextOutlined />, subscriptionRequired: true, hasPermission: hasPermissionReportsKm })}
        {renderMenuItem({ key: 'reports/shifts', title: 'Shifts', icon: <FileTextOutlined />, subscriptionRequired: true, hasPermission: hasPermissionReportsShift })}
        {renderMenuItem({ key: 'reports/tax', title: 'Tax', icon: <FileTextOutlined />, subscriptionRequired: true, hasPermission: hasPermissionReportsTax })}
      </Menu.SubMenu>
      {renderAdminMenuItem({ key: 'users', title: 'Users', icon: <UserOutlined /> })}
      {renderAdminMenuItem({ key: 'organizations', title: 'Organizations', icon: <BankOutlined /> })}
    </Menu>
  );
}
